import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { Crosshair, Layers, MapPin, X } from 'lucide-react';

import { MapCircle } from '@/components/shared';
import { cn } from '@/lib/utils';

/** Pin glyph shape. `challenge` = round pin, `stack` = diamond, `artifact` = ring. */
export type PinKind = 'challenge' | 'stack' | 'artifact';

/** A single map pin. `latitude`/`longitude` null means "not yet placed". */
export interface PinMapItem {
  kind: PinKind;
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  color: string;
  /** Optional sub-label shown in the marker tooltip (e.g. group name). */
  groupName?: string;
  /** Optional detection radius (meters) drawn when the pin is selected. */
  radius?: number | null;
}

/** One legend row. */
export interface PinMapLegendEntry {
  label: string;
  color: string;
  shape?: 'circle' | 'diamond';
}

export interface PinMapEditorProps {
  items: PinMapItem[];
  /** Drag + click-to-place disabled when false (read-only map). */
  editable: boolean;
  /** Persist a dragged pin's new coordinates. */
  onMove: (item: PinMapItem, latitude: number, longitude: number) => void;
  /** Persist a placed (previously unplaced) pin. Defaults to `onMove`. */
  onPlace?: (item: PinMapItem, latitude: number, longitude: number) => void;
  legend?: PinMapLegendEntry[];
  googleMapsApiKey?: string;
  /** Container height class (default `h-[560px]`). Ignored when `fillViewport` is set. */
  heightClassName?: string;
  /**
   * Size the map to fill the viewport below its rendered position, leaving the
   * same gap at the bottom as the page's side padding. Use on surfaces where
   * the map is the last content on screen.
   */
  fillViewport?: boolean;
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 13;

// `fillViewport` sizing: gap left below the map — matches the page container's
// p-6 side padding so the map is inset evenly — and a floor for small windows.
const FILL_VIEWPORT_BOTTOM_GAP_PX = 24;
const FILL_VIEWPORT_MIN_HEIGHT_PX = 420;

/**
 * Measures the ref'd element's position and returns the height that makes it
 * end `FILL_VIEWPORT_BOTTOM_GAP_PX` above the nearest scroll container's bottom
 * edge (the page's scroll area ends above the window bottom), falling back to
 * the viewport. A fixed calc() offset can't do this — the content above the map
 * can wrap to a varying height.
 */
function useFillViewportHeight(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(FILL_VIEWPORT_MIN_HEIGHT_PX);

  useLayoutEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    // Use the OUTERMOST scroll ancestor: it is the page-level scroll area whose
    // bottom edge is the visible bottom of the page. Nearer scroll wrappers
    // (e.g. a tab body) are often content-sized, so measuring against them is
    // circular — the map's own height would define the space it "fills".
    let scroller: HTMLElement | null = null;
    for (let node = el.parentElement; node; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scroller = node;
      }
    }

    // Scroll-position-independent: compute the map's offset from the top of
    // the scrolled content, not the viewport, so measuring while scrolled (or
    // before async content above settles) still yields the scrolled-to-top fit.
    const update = () => {
      const rect = el.getBoundingClientRect();
      const available = scroller
        ? scroller.clientHeight -
          (rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop) -
          FILL_VIEWPORT_BOTTOM_GAP_PX
        : window.innerHeight - (rect.top + window.scrollY) - FILL_VIEWPORT_BOTTOM_GAP_PX;
      setHeight(Math.max(FILL_VIEWPORT_MIN_HEIGHT_PX, Math.round(available)));
    };

    update();
    // Content above the map can load/settle after mount (hero, tab chrome) —
    // watch the scroll area and its content, not just the window.
    const resizeObserver = new ResizeObserver(update);
    for (const node of [scroller, scroller?.firstElementChild]) {
      if (node) resizeObserver.observe(node);
    }
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      resizeObserver.disconnect();
    };
  }, [enabled]);

  return { ref, height };
}

function FitBounds({ items }: { items: PinMapItem[] }) {
  const map = useMap();
  const placed = items.filter((item) => item.latitude !== null && item.longitude !== null);
  // Fit once per placed-count change, not on every drag.
  const placedCount = placed.length;

  useEffect(() => {
    if (!map || placedCount === 0) return;
    const bounds = new google.maps.LatLngBounds();
    placed.forEach((item) => bounds.extend({ lat: item.latitude!, lng: item.longitude! }));
    map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, placedCount]);

  return null;
}

function PinGlyph({ kind, color, dimmed }: { kind: PinKind; color: string; dimmed?: boolean }) {
  const base = 'border-2 border-white shadow-md';
  if (kind === 'stack') {
    return (
      <div
        className={cn('h-6 w-6 rotate-45', base, dimmed && 'opacity-50')}
        style={{ backgroundColor: color, borderRadius: 6 }}
      />
    );
  }
  if (kind === 'artifact') {
    return (
      <div
        className={cn('flex h-7 w-7 items-center justify-center rounded-full', base, dimmed && 'opacity-50')}
        style={{ backgroundColor: color }}
      >
        <div className="h-2.5 w-2.5 rounded-full bg-white/80" />
      </div>
    );
  }
  return (
    <div
      className={cn('flex h-7 w-7 items-center justify-center rounded-full', base, dimmed && 'opacity-50')}
      style={{ backgroundColor: color }}
    >
      <MapPin className="h-4 w-4 text-white" />
    </div>
  );
}

function PinMapEditorInner({
  items,
  editable,
  onMove,
  onPlace,
  legend,
  heightClassName = 'h-[560px]',
  fillViewport = false,
}: Omit<PinMapEditorProps, 'googleMapsApiKey'>) {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight(fillViewport);
  const mapId = useId();
  const place = onPlace ?? onMove;
  const [armedItem, setArmedItem] = useState<PinMapItem | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const placed = useMemo(
    () => items.filter((item) => item.latitude !== null && item.longitude !== null),
    [items],
  );
  const missing = useMemo(
    () => items.filter((item) => item.latitude === null || item.longitude === null),
    [items],
  );

  const defaultCenter = useMemo(() => {
    if (placed.length === 0) return DEFAULT_CENTER;
    return {
      lat: placed.reduce((sum, item) => sum + item.latitude!, 0) / placed.length,
      lng: placed.reduce((sum, item) => sum + item.longitude!, 0) / placed.length,
    };
  }, [placed]);

  const selected = placed.find((item) => item.id === selectedId);

  return (
    <div
      ref={fillRef}
      className={cn('relative overflow-hidden rounded-xl border', !fillViewport && heightClassName)}
      style={fillViewport ? { height: fillHeight } : undefined}
    >
      <Map
        mapId={mapId}
        defaultCenter={defaultCenter}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        zoomControl
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        className="h-full w-full"
        onClick={(event) => {
          const latLng = event.detail.latLng;
          if (armedItem && latLng && editable) {
            place(armedItem, latLng.lat, latLng.lng);
            setArmedItem(null);
          } else {
            setSelectedId(null);
          }
        }}
      >
        <FitBounds items={items} />

        {placed.map((item) => (
          <AdvancedMarker
            key={`${item.kind}-${item.id}`}
            position={{ lat: item.latitude!, lng: item.longitude! }}
            title={`${item.name}${item.groupName ? ` · ${item.groupName}` : ''}`}
            draggable={editable}
            onClick={() => setSelectedId(item.id)}
            onDragEnd={(event) => {
              const position = event.latLng;
              if (!position) return;
              onMove(item, position.lat(), position.lng());
            }}
          >
            <PinGlyph kind={item.kind} color={item.color} dimmed={Boolean(armedItem)} />
          </AdvancedMarker>
        ))}

        {selected?.radius ? (
          <MapCircle
            center={{ lat: selected.latitude!, lng: selected.longitude! }}
            radius={selected.radius}
            fillColor={selected.color}
            fillOpacity={0.12}
            strokeColor={selected.color}
            strokeOpacity={0.5}
            strokeWeight={1.5}
          />
        ) : null}
      </Map>

      {/* Placement banner */}
      {armedItem ? (
        <div className="absolute top-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white shadow-lg">
          <Crosshair className="h-4 w-4" />
          Click the map to place “{armedItem.name}”
          <button
            type="button"
            onClick={() => setArmedItem(null)}
            aria-label="Cancel placement"
            className="ml-1 rounded-full p-0.5 hover:bg-white/20"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      {/* Legend */}
      {legend && legend.length > 0 ? (
        <div className="absolute bottom-3 left-3 max-h-48 space-y-1 overflow-y-auto rounded-lg border bg-white/95 px-3 py-2 text-xs shadow-sm backdrop-blur-sm">
          {legend.map((entry) => (
            <div key={`${entry.label}-${entry.color}`} className="flex items-center gap-2">
              <span
                className={cn('h-2.5 w-2.5 shrink-0', entry.shape === 'diamond' ? 'rotate-45' : 'rounded-full')}
                style={{ backgroundColor: entry.color, ...(entry.shape === 'diamond' ? { borderRadius: 2 } : {}) }}
              />
              <span className="text-foreground">{entry.label}</span>
            </div>
          ))}
          {editable ? (
            <p className="border-t pt-1 font-mono text-[0.625rem] tracking-wide text-muted-foreground">
              Drag pins to move
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Missing coordinates panel */}
      {missing.length > 0 && editable ? (
        <div className="absolute top-3 right-3 w-64 rounded-lg border bg-white/95 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Layers className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="font-mono text-[0.6875rem] tracking-[0.15em] text-muted-foreground uppercase">
              Needs a location ({missing.length})
            </p>
          </div>
          <div className="max-h-52 divide-y overflow-y-auto">
            {missing.map((item) => (
              <button
                key={`${item.kind}-${item.id}`}
                type="button"
                onClick={() => setArmedItem(item)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                  armedItem?.id === item.id && 'bg-primary/10',
                )}
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="min-w-0 flex-1 truncate text-foreground">{item.name}</span>
                <span className="font-mono text-[0.625rem] text-muted-foreground uppercase">
                  {item.kind}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Generic all-pins map editor: renders every item as a colored, draggable pin,
 * lists unplaced items in a "Needs a location" panel (arm one, click the map to
 * place it), and reports moves/placements via `onMove`/`onPlace`. Knows nothing
 * about events/templates/challenges — callers build `items` and persist changes.
 */
export function PinMapEditor({ googleMapsApiKey, ...props }: PinMapEditorProps) {
  if (!googleMapsApiKey) {
    return (
      <div className="flex h-64 flex-col items-center justify-center rounded-xl border bg-muted/50 text-muted-foreground">
        <MapPin className="mb-1 h-6 w-6" />
        <span className="text-sm">Map unavailable — no API key configured</span>
      </div>
    );
  }

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <PinMapEditorInner {...props} />
    </APIProvider>
  );
}
